import ELK from 'elkjs/lib/elk.bundled.js';
import { Node, Edge } from 'reactflow';
import { LayoutDirection } from '../types';
import {
  ClusterBounds,
  LayoutOptions,
  LayoutResult,
} from './layoutEngine.types';

/** ELK input types — geneste graph */
interface ElkNodeInput {
  id: string;
  width: number;
  height: number;
  children?: ElkNodeInput[];
  layoutOptions?: Record<string, string>;
}

interface ElkEdgeInput {
  id: string;
  sources: string[];
  targets: string[];
}

interface ElkGraphInput {
  id: string;
  layoutOptions: Record<string, string>;
  children: ElkNodeInput[];
  edges: ElkEdgeInput[];
}

/** ELK output types — coordinaten zijn relatief aan parent compound */
interface ElkNodeOutput {
  id: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  children?: ElkNodeOutput[];
}

const NODE_WIDTH = 220;
const NODE_HEIGHT = 140;
const CLUSTER_PADDING = 30;

const CLUSTER_COLORS = [
  { fill: '#E3F2FD', border: '#90CAF9' },
  { fill: '#E8F5E9', border: '#A5D6A7' },
  { fill: '#FFF3E0', border: '#FFCC80' },
  { fill: '#F3E5F5', border: '#CE93D8' },
  { fill: '#FCE4EC', border: '#F48FB1' },
  { fill: '#E0F7FA', border: '#80DEEA' },
];

const elk = new ELK();

function mapDirection(direction: LayoutDirection): string {
  switch (direction) {
    case 'TB':
      return 'DOWN';
    case 'BT':
      return 'UP';
    case 'LR':
      return 'RIGHT';
    case 'RL':
      return 'LEFT';
    default:
      return 'DOWN';
  }
}

function isExternalNode(node: Node | undefined): boolean {
  if (!node) return false;
  if (node.id.startsWith('ext-')) return true;
  const country = (node.data && (node.data as any).country) as
    | string
    | undefined;
  return country === 'EXT';
}

export function assignClusters(
  nodes: Node[],
  edges: Edge[]
): Map<string, string> {
  const nodeMap = new Map<string, Node>();
  nodes.forEach((n) => nodeMap.set(n.id, n));

  // Build primary parent map: target -> first non-external source
  const primaryParent = new Map<string, string>();
  for (const edge of edges) {
    const sourceNode = nodeMap.get(edge.source);
    if (isExternalNode(sourceNode)) continue;
    if (!primaryParent.has(edge.target)) {
      primaryParent.set(edge.target, edge.source);
    }
  }

  const clusterMap = new Map<string, string>();
  for (const node of nodes) {
    if (isExternalNode(node)) {
      clusterMap.set(node.id, 'external');
      continue;
    }
    // Walk up parent chain
    const visited = new Set<string>();
    let current = node.id;
    while (true) {
      if (visited.has(current)) break;
      visited.add(current);
      const parent = primaryParent.get(current);
      if (!parent) break;
      const parentNode = nodeMap.get(parent);
      if (!parentNode || isExternalNode(parentNode)) break;
      current = parent;
    }
    clusterMap.set(node.id, current);
  }

  return clusterMap;
}

/**
 * Bouwt een hiërarchische ELK graph waarin nodes per cluster gegroepeerd
 * zijn in compound (parent) nodes. Externals blijven op root-level.
 */
export function buildHierarchicalElkGraph(
  nodes: Node[],
  edges: Edge[],
  direction: LayoutDirection
): ElkGraphInput {
  const clusterMap = assignClusters(nodes, edges);

  // Groep non-external nodes per cluster
  const compoundChildren = new Map<string, ElkNodeInput[]>();
  const externalsAtRoot: ElkNodeInput[] = [];

  for (const node of nodes) {
    const elkNode: ElkNodeInput = {
      id: node.id,
      width: (node as any).width || NODE_WIDTH,
      height: (node as any).height || NODE_HEIGHT,
    };

    const clusterId = clusterMap.get(node.id);
    if (clusterId === 'external' || !clusterId) {
      externalsAtRoot.push(elkNode);
    } else {
      if (!compoundChildren.has(clusterId)) {
        compoundChildren.set(clusterId, []);
      }
      compoundChildren.get(clusterId)!.push(elkNode);
    }
  }

  // Build compounds (één per top-level root)
  const compounds: ElkNodeInput[] = [];
  // Array.from i.p.v. .entries() iterator — onze tsconfig target=es5
  // ondersteunt geen direct Map iteratie zonder downlevelIteration.
  for (const [clusterId, children] of Array.from(compoundChildren.entries())) {
    compounds.push({
      id: `cluster:${clusterId}`,
      width: 0, // ELK berekent dit zelf
      height: 0,
      children,
      layoutOptions: {
        'elk.algorithm': 'layered',
        'elk.direction': mapDirection(direction),
        'elk.layered.spacing.nodeNodeBetweenLayers': '160',
        'elk.spacing.nodeNode': '70',
        'elk.layered.layering.strategy': 'NETWORK_SIMPLEX',
        'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
        'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
        'elk.layered.nodePlacement.favorStraightEdges': 'true',
      },
    });
  }

  return {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': mapDirection(direction),
      'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
      'elk.spacing.componentComponent': '140',
      'elk.spacing.nodeNode': '100',
      'elk.layered.spacing.nodeNodeBetweenLayers': '160',
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.spacing.edgeNode': '50',
      'elk.spacing.edgeEdge': '20',
    },
    children: [...compounds, ...externalsAtRoot],
    edges: edges.map((e) => ({
      id: e.id,
      sources: [e.source],
      targets: [e.target],
    })),
  };
}

/** Minimum aantal nodes voordat een cluster zijn eigen achtergrond krijgt. */
const MIN_CLUSTER_SIZE = 3;

/**
 * Read cluster bounds direct uit de geneste ELK output. Compound nodes
 * hebben hun eigen x/y/width/height na ELK-layout — dat zijn de exacte,
 * niet-overlappende bounds.
 */
function readClusterBoundsFromElk(
  elkRoot: ElkNodeOutput,
  nodeMap: Map<string, Node>
): ClusterBounds[] {
  const result: ClusterBounds[] = [];

  // Sorteer compounds op size (groot → klein) voor deterministische kleur-toewijzing
  const compounds = (elkRoot.children || [])
    .filter((c) => c.id.startsWith('cluster:') && c.children && c.children.length >= MIN_CLUSTER_SIZE)
    .sort((a, b) => (b.children?.length || 0) - (a.children?.length || 0));

  let colorIdx = 0;
  for (const compound of compounds) {
    const rootId = compound.id.replace('cluster:', '');
    const rootNode = nodeMap.get(rootId);
    const rootName = (rootNode?.data as any)?.name;
    const label = rootName || rootId;
    const color = CLUSTER_COLORS[colorIdx++ % CLUSTER_COLORS.length];

    result.push({
      id: rootId,
      label,
      x: (compound.x ?? 0) - CLUSTER_PADDING,
      y: (compound.y ?? 0) - CLUSTER_PADDING,
      width: (compound.width ?? 0) + CLUSTER_PADDING * 2,
      height: (compound.height ?? 0) + CLUSTER_PADDING * 2,
      color: color.fill,
      borderColor: color.border,
    });
  }

  return result;
}

/**
 * Flatten een geneste ELK output graph naar een Map van id → absolute positie.
 * ELK levert coordinaten relatief aan de containing compound; we recursen
 * door en addeer parent-offsets zodat elke node zijn positie in de root-
 * coordinatensysteem krijgt.
 */
export function flattenElkPositions(
  root: ElkNodeOutput,
  parentX: number = 0,
  parentY: number = 0
): Map<string, { x: number; y: number }> {
  const result = new Map<string, { x: number; y: number }>();

  for (const child of root.children || []) {
    const absX = parentX + (child.x ?? 0);
    const absY = parentY + (child.y ?? 0);
    result.set(child.id, { x: absX, y: absY });

    if (child.children && child.children.length > 0) {
      const nested = flattenElkPositions(child, absX, absY);
      nested.forEach((pos, id) => result.set(id, pos));
    }
  }

  return result;
}

export async function layoutGraph(
  nodes: Node[],
  edges: Edge[],
  direction: LayoutDirection,
  options: LayoutOptions = {}
): Promise<LayoutResult> {
  if (!nodes || nodes.length === 0) {
    return { nodes: [], edges: edges || [], clusters: [] };
  }

  // Hierarchical layout via compound nodes — clusters worden als parent-nodes
  // gemodelleerd zodat ELK hun bounds direct berekent.
  const elkGraph = buildHierarchicalElkGraph(nodes, edges, direction);

  const layoutResult = await elk.layout(elkGraph as any);

  const positionMap = flattenElkPositions(layoutResult as any);

  const laidOutNodes: Node[] = nodes.map((n) => {
    const pos = positionMap.get(n.id) || { x: 0, y: 0 };
    return { ...n, position: { x: pos.x, y: pos.y } };
  });

  // Extract bend-points + start/end uit ELK output voor orthogonal routing.
  //
  // ELK rekent zelf uit waar een edge een node verlaat/binnenkomt (de
  // 'startPoint' / 'endPoint' in de section). Die punten zijn cruciaal:
  // als de edge-renderer een ANDER anchor gebruikt (bv. eigen middel-van-
  // node-rand berekening), dan ontstaat een schuine join-segment tussen
  // het anchor en het eerste/laatste bend-point — die schuine lijn kan
  // alsnog door andere nodes lopen.
  //
  // Daarom geven we BEIDE door: bend-points + start/end. De renderer
  // gebruikt ELK's start/end als bend-points aanwezig zijn.
  const elkEdges = ((layoutResult as any).edges || []) as Array<{
    id: string;
    sections?: Array<{
      startPoint?: { x: number; y: number };
      endPoint?: { x: number; y: number };
      bendPoints?: Array<{ x: number; y: number }>;
    }>;
  }>;

  type ElkRoute = {
    startPoint?: { x: number; y: number };
    endPoint?: { x: number; y: number };
    bendPoints: Array<{ x: number; y: number }>;
  };
  const routeById = new Map<string, ElkRoute>();
  elkEdges.forEach((ee) => {
    const section = ee.sections?.[0];
    routeById.set(ee.id, {
      startPoint: section?.startPoint,
      endPoint: section?.endPoint,
      bendPoints: section?.bendPoints || [],
    });
  });

  const laidOutEdges: Edge[] = edges.map((e) => {
    const route = routeById.get(e.id);
    return {
      ...e,
      data: {
        ...(e.data || {}),
        bendPoints: route?.bendPoints || [],
        elkStart: route?.startPoint,
        elkEnd: route?.endPoint,
      },
    };
  });

  let clusters: ClusterBounds[] = [];
  if (options.groupByRoot) {
    const nodeMap = new Map(laidOutNodes.map((n) => [n.id, n]));
    clusters = readClusterBoundsFromElk(layoutResult as any, nodeMap);
  }

  return {
    nodes: laidOutNodes,
    edges: laidOutEdges,
    clusters,
  };
}
